import React from "react";
import { DesktopFrame, PhoneFrame, Chip } from "./frames";
import { CheckCircle2, Circle, FileText, Video, Award, Clock, ChevronRight } from "lucide-react";

const Side = () => (
  <div className="hidden w-40 shrink-0 flex-col bg-[#173A2C] p-4 text-white md:flex">
    <div className="font-serif text-base">Candidate</div>
    <div className="mt-4 space-y-1 text-[11px]">
      {["Overview", "Profile editor", "Media & documents", "Approval status", "My applications"].map((n, i) => (
        <div key={n} className={`rounded-sm px-2 py-1.5 ${i === 0 ? "bg-white/15 text-white" : "text-white/70"}`}>{n}</div>
      ))}
    </div>
    <div className="mt-auto rounded-sm bg-white/10 p-2 text-[10px] text-white/80">Alias in employer view: <b className="text-[#C4A46B]">HG-104</b></div>
  </div>
);

const Checklist = () => (
  <div className="rounded-md border border-[#E8E4DB] bg-white p-4">
    <div className="flex items-center justify-between">
      <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#C4A46B]">Profile completeness</div>
      <span className="text-[13px] font-bold text-[#1F4A38]">78%</span>
    </div>
    <div className="mt-2 h-2 w-full rounded-full bg-[#E8E4DB]"><div className="h-2 w-[78%] rounded-full bg-[#1F4A38]" /></div>
    <div className="mt-3 grid grid-cols-2 gap-1.5 text-[11px]">
      {[["Identity & contact", true], ["Experience & skills", true], ["Languages", true], ["Availability", true], ["CV uploaded", true], ["Intro video", false], ["Preferred countries", true], ["Evidence (2/3)", false]].map(([n, done]) => (
        <div key={n} className="flex items-center gap-1.5 text-[#2C2A29]">
          {done ? <CheckCircle2 className="h-3.5 w-3.5 text-[#2E6B4B]" /> : <Circle className="h-3.5 w-3.5 text-[#D28E3D]" />}
          <span className={done ? "" : "text-[#5C5855]"}>{n}</span>
        </div>
      ))}
    </div>
  </div>
);

const StatusStrip = () => (
  <div className="flex items-center gap-2 rounded-md border border-[#2E6B4B]/40 bg-[#2E6B4B]/10 p-3">
    <CheckCircle2 className="h-4 w-4 text-[#2E6B4B]" />
    <div className="text-[11px] text-[#2E6B4B]">Status: <b>Approved</b> — visible to verified employers. You can now apply to jobs.</div>
  </div>
);

const Journey = () => {
  const stages = ["Applied", "Screening", "Interview", "Offer", "Documents", "ZAV", "Visa", "Arrival"];
  const active = 2;
  return (
    <div className="rounded-md border border-[#E8E4DB] bg-white p-4">
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-semibold text-[#2C2A29]">Head Groom — Bavaria, Germany</div>
        <Chip tone="amber">Interview</Chip>
      </div>
      <div className="mt-3 flex items-center">
        {stages.map((s, i) => (
          <React.Fragment key={s}>
            <div className="flex flex-col items-center">
              <div className={`flex h-5 w-5 items-center justify-center rounded-full text-[8px] ${i < active ? "bg-[#2E6B4B] text-white" : i === active ? "bg-[#C4A46B] text-[#173A2C]" : "border border-[#E8E4DB] bg-white text-[#5C5855]"}`}>{i < active ? "✓" : i + 1}</div>
              <span className="mt-1 w-10 text-center text-[7px] leading-tight text-[#5C5855]">{s}</span>
            </div>
            {i < stages.length - 1 && <div className={`h-0.5 flex-1 ${i < active ? "bg-[#2E6B4B]" : "bg-[#E8E4DB]"}`} />}
          </React.Fragment>
        ))}
      </div>
      <div className="mt-3 rounded-sm bg-[#FDFBF7] p-2 text-[10px] text-[#5C5855]">
        Next action: <b className="text-[#2C2A29]">You</b> — confirm interview slot · Due Fri · Updated 1d ago
      </div>
    </div>
  );
};

const Uploader = () => (
  <div className="rounded-md border border-[#E8E4DB] bg-white p-4">
    <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#C4A46B]">Media & documents</div>
    <div className="mt-2 space-y-1.5 text-[11px]">
      {[[FileText, "CV.pdf", "1 active · max 5 MB", "green"], [Award, "Certificate.jpg", "12/15 files · max 10 MB", "green"], [Video, "Intro clip", "≤90s · ≤100 MB · uploading 62%", "amber"]].map(([Icon, n, sub, tone], i) => (
        <div key={i} className="flex items-center gap-2 rounded-sm border border-[#E8E4DB] p-2">
          <Icon className="h-4 w-4 text-[#1F4A38]" />
          <div className="flex-1"><div className="text-[#2C2A29]">{n}</div><div className="text-[9px] text-[#5C5855]">{sub}</div></div>
          <Chip tone={tone}>{tone === "amber" ? "Uploading" : "Stored"}</Chip>
        </div>
      ))}
    </div>
    <div className="mt-2 text-[9px] text-[#5C5855]">Private storage · no public URLs · checked in browser & server</div>
  </div>
);

const ProfCard = () => (
  <div className="rounded-md border border-[#E8E4DB] bg-white p-4">
    <div className="flex items-center justify-between">
      <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#C4A46B]">Professions & role skills</div>
      <div className="flex gap-1"><Chip tone="gold">Groomer</Chip><Chip tone="gold">Rider</Chip></div>
    </div>
    <div className="mt-2 space-y-1.5">
      <div className="flex flex-wrap items-center gap-1 text-[10px]"><span className="w-14 text-[#5C5855]">Groomer</span><Chip tone="slate">Clipping</Chip><Chip tone="slate">Plaiting</Chip><Chip tone="slate">Show prep</Chip></div>
      <div className="flex flex-wrap items-center gap-1 text-[10px]"><span className="w-14 text-[#5C5855]">Rider</span><Chip tone="slate">Dressage</Chip><Chip tone="slate">Show jumping</Chip></div>
    </div>
  </div>
);

export const CandidateDesktop = () => (
  <DesktopFrame testid="mock-candidate-desktop" url="equiwelt.com/candidate">
    <div className="flex h-full bg-[#FDFBF7]">
      <Side />
      <div className="flex-1 space-y-3 overflow-y-auto thin-scroll p-4">
        <div className="font-serif text-xl text-[#173A2C]">Welcome back, Maria</div>
        <ProfCard />
        <StatusStrip />
        <Checklist />
        <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#C4A46B]">Application journey</div>
        <Journey />
        <Uploader />
      </div>
    </div>
  </DesktopFrame>
);

export const CandidateMobile = () => (
  <PhoneFrame testid="mock-candidate-mobile">
    <div className="h-full overflow-y-auto thin-scroll bg-[#FDFBF7]">
      <div className="bg-[#173A2C] px-4 py-3 text-white"><div className="font-serif text-base">My profile</div><div className="text-[10px] text-white/70">Alias HG-104 · Groomer, Rider</div></div>
      <div className="space-y-3 p-3">
        <ProfCard />
        <StatusStrip />
        <Checklist />
        <Journey />
      </div>
      <div className="sticky bottom-0 flex justify-around border-t border-[#E8E4DB] bg-white/95 py-2 text-[9px] text-[#5C5855] backdrop-blur">
        <span className="font-semibold text-[#1F4A38]">Home</span><span>Profile</span><span>Media</span><span>Journey</span>
      </div>
    </div>
  </PhoneFrame>
);
